import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { v4 as uuid_v4 } from 'uuid';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CatalogRoom,
  ConflictResolution,
  ExistingBlackoutDate,
  ExtractedDateRange,
  ISODateRange,
  ManualRoom,
  RateConflictInfo,
  StopsellApplyBlock,
  StopsellApplyPayload,
  StopsellApplyRange,
  StopsellExtraction,
} from '~/types/stopsell';

import { buildStopsellConfirmSummary, rateConflictKey } from '~/utils/stopsellConfirmSummary';
import { findRateConflict, sliceResolutionsByRoom } from '~/utils/stopsellConflicts';
import { pluralizeToString } from '~/utils/stringUtils';

import AvailabilityConfirmModal from './AvailabilityConfirmModal';
import AvailabilityManualRoomsSection from './AvailabilityManualRoomsSection';
import StopsellManualRoomCard from './StopsellManualRoomCard';
import StopsellRoomGroupRow from './StopsellRoomGroupRow';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (payload: StopsellApplyPayload) => void;
  extraction: StopsellExtraction;
  fileName: string;
  catalogRooms: Array<CatalogRoom>;
  isBlackoutError?: boolean;
  isApplying?: boolean;
}

function buildRateOperations(
  dateRange: ISODateRange,
  rates: Array<{ id: string }>,
  blackoutDates: Array<ExistingBlackoutDate>,
  getResolution: (rateId: string) => ConflictResolution | null,
): { blocksToDelete: Array<StopsellApplyBlock>; rangesToCreate: Array<StopsellApplyRange> } {
  const blocksToDelete: Array<StopsellApplyBlock> = [];
  const rangesToCreate: Array<StopsellApplyRange> = [];

  for (const rate of rates) {
    const conflict = findRateConflict(dateRange, rate.id, blackoutDates);
    const resolution = getResolution(rate.id);

    if (resolution === 'skipped') {
      continue;
    }
    if (conflict.status === 'covered_by_existing') {
      continue;
    }
    if (conflict.status !== 'clear' && (resolution === 'ignored' || !resolution)) {
      continue;
    }

    const range =
      conflict.status === 'partial_overlap' && resolution === 'extending' && conflict.mergedRange
        ? conflict.mergedRange
        : { start: dateRange.start, end: dateRange.end };

    if (
      (conflict.status === 'covers_existing' || conflict.status === 'partial_overlap') &&
      (resolution === 'overriding' || resolution === 'extending') &&
      conflict.existingBlock
    ) {
      blocksToDelete.push(conflict.existingBlock);
    }

    rangesToCreate.push({ ...range, roomRateId: rate.id });
  }

  return { blocksToDelete, rangesToCreate };
}

function initLocalDates(
  extraction: StopsellExtraction,
  catalogRooms: Array<CatalogRoom>,
): Array<Array<ExtractedDateRange>> {
  return extraction.rooms.map((room) => {
    const catalogRoom = catalogRooms.find((c) => c.id === room.roomTypeId);
    const blackoutDates = catalogRoom?.blackoutDates ?? [];
    const rates = catalogRoom?.roomRates ?? [];
    return room.dates.map((date) => {
      const hasActionableConflict = rates.some((rate) => {
        const conflict = findRateConflict(date, rate.id, blackoutDates);
        return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
      });
      return { id: uuid_v4(), start: date.start, end: date.end, selected: !hasActionableConflict };
    });
  });
}

export default function StopsellReviewModal({
  open,
  onClose,
  onApply,
  extraction,
  fileName,
  catalogRooms,
  isBlackoutError = false,
  isApplying = false,
}: Props) {
  const [roomTypeOverrides, setRoomTypeOverrides] = useState<Record<number, string>>({});
  const [resolutions, setResolutions] = useState<Record<string, ConflictResolution>>({});
  const [manualResolutions, setManualResolutions] = useState<Record<string, ConflictResolution>>({});
  const [manualRooms, setManualRooms] = useState<Array<ManualRoom>>([]);
  const [localDates, setLocalDates] = useState<Array<Array<ExtractedDateRange>>>(() =>
    initLocalDates(extraction, catalogRooms),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setResolutions({});
      setManualResolutions({});
      setRoomTypeOverrides({});
      setManualRooms([]);
      setLocalDates(initLocalDates(extraction, catalogRooms));
      setConfirmOpen(false);
    }
    prevOpenRef.current = open;
  }, [open, extraction, catalogRooms]);

  const rateConflicts = useMemo<Array<Array<Array<RateConflictInfo>>>>(
    () =>
      localDates.map((roomDates, roomIndex) => {
        const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
        const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
        const blackoutDates = catalogRoom?.blackoutDates ?? [];
        const rates = catalogRoom?.roomRates ?? [];
        return roomDates.map((date) => rates.map((rate) => findRateConflict(date, rate.id, blackoutDates)));
      }),
    [localDates, catalogRooms, roomTypeOverrides, extraction.rooms],
  );

  const unresolvedConflicts = useMemo(() => {
    const extractedUnresolved = rateConflicts.reduce((sum, roomConflicts, roomIndex) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const rates = catalogRooms.find((c) => c.id === roomTypeId)?.roomRates ?? [];
      return (
        sum +
        roomConflicts.reduce((dateSum, dateConflicts, dateIndex) => {
          if (!localDates[roomIndex]?.[dateIndex]?.selected) {
            return dateSum;
          }
          const dateId = localDates[roomIndex]?.[dateIndex]?.id ?? '';
          return (
            dateSum +
            dateConflicts.filter((conflict, rateIndex) => {
              if (conflict.status === 'clear' || conflict.status === 'covered_by_existing') {
                return false;
              }
              const rateId = rates[rateIndex]?.id;
              if (!rateId) {
                return false;
              }
              return !resolutions[rateConflictKey(roomIndex, dateId, rateId)];
            }).length
          );
        }, 0)
      );
    }, 0);
    const manualUnresolved = manualRooms.reduce((sum, room, roomIndex) => {
      const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
      const blackoutDates = catalogRoom?.blackoutDates ?? [];
      const rates = catalogRoom?.roomRates ?? [];
      return (
        sum +
        room.dates.reduce((dateSum, date, dateIndex) => {
          if (!date.selected) {
            return dateSum;
          }
          return (
            dateSum +
            rates.filter((rate) => {
              const conflict = findRateConflict(date, rate.id, blackoutDates);
              if (conflict.status === 'clear' || conflict.status === 'covered_by_existing') {
                return false;
              }
              return !manualResolutions[rateConflictKey(roomIndex, date.id, rate.id)];
            }).length
          );
        }, 0)
      );
    }, 0);

    return extractedUnresolved + manualUnresolved;
  }, [
    rateConflicts,
    resolutions,
    roomTypeOverrides,
    extraction.rooms,
    catalogRooms,
    manualRooms,
    manualResolutions,
    localDates,
  ]);

  const allSelected =
    localDates.every((room) => room.every((date) => date.selected)) &&
    manualRooms.every((room) => room.dates.every((d) => d.selected));
  const anySelected =
    localDates.some((room) => room.some((date) => date.selected)) ||
    manualRooms.some((room) => room.dates.some((d) => d.selected));
  const selectAllIndeterminate = anySelected && !allSelected;

  const extractedRoomTypeIds = useMemo(
    () =>
      new Set(
        extraction.rooms
          .map((room, roomIndex) => roomTypeOverrides[roomIndex] ?? room.roomTypeId)
          .filter((id): id is string => id !== null),
      ),
    [extraction.rooms, roomTypeOverrides],
  );

  const allSelectedRoomsMapped = extraction.rooms.every((room, roomIndex) => {
    const isRoomSelected = localDates[roomIndex]?.some((date) => date.selected);
    if (!isRoomSelected) {
      return true;
    }
    return !!(room.roomTypeId ?? roomTypeOverrides[roomIndex]);
  });

  const selectedRoomCount =
    localDates.filter((room) => room.some((date) => date.selected)).length +
    manualRooms.filter((room) => room.dates.some((d) => d.selected)).length;

  const selectedDateCount =
    localDates.reduce((sum, room) => sum + room.filter((date) => date.selected).length, 0) +
    manualRooms.reduce((sum, room) => sum + room.dates.filter((d) => d.selected).length, 0);

  const handleSelectAll = useCallback((checked: boolean) => {
    setLocalDates((prev) => prev.map((room) => room.map((date) => ({ ...date, selected: checked }))));
    setManualRooms((prev) =>
      prev.map((room) => ({ ...room, dates: room.dates.map((d) => ({ ...d, selected: checked })) })),
    );
  }, []);

  const handleSelectAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleSelectAll(e.target.checked),
    [handleSelectAll],
  );

  const handleRoomToggleAll = useCallback((roomIndex: number, value: boolean) => {
    setLocalDates((prev) =>
      prev.map((room, roomIdx) => (roomIdx === roomIndex ? room.map((date) => ({ ...date, selected: value })) : room)),
    );
  }, []);

  const handleDateChange = useCallback((roomIndex: number, dateIndex: number, value: boolean) => {
    setLocalDates((prev) =>
      prev.map((room, roomIdx) =>
        roomIdx === roomIndex
          ? room.map((date, dateIdx) => (dateIdx === dateIndex ? { ...date, selected: value } : date))
          : room,
      ),
    );
  }, []);

  const handleRoomTypeOverride = useCallback(
    (roomIndex: number, roomTypeId: string) => {
      setRoomTypeOverrides((prev) => ({ ...prev, [roomIndex]: roomTypeId }));

      setResolutions((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${roomIndex}-`)) {
            delete next[key];
          }
        });
        return next;
      });

      const newCatalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
      const newBlackoutDates = newCatalogRoom?.blackoutDates ?? [];
      const newRates = newCatalogRoom?.roomRates ?? [];
      setLocalDates((prev) =>
        prev.map((roomSelected, roomIdx) => {
          if (roomIdx !== roomIndex) {
            return roomSelected;
          }
          return roomSelected.map((date) => {
            const hasActionableConflict = newRates.some((rate) => {
              const conflict = findRateConflict(date, rate.id, newBlackoutDates);
              return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
            });
            return { ...date, selected: !hasActionableConflict };
          });
        }),
      );
    },
    [catalogRooms],
  );

  const handleResolve = useCallback((roomIndex: number, dateId: string, rateId: string, status: ConflictResolution) => {
    setResolutions((prev) => ({ ...prev, [rateConflictKey(roomIndex, dateId, rateId)]: status }));
  }, []);

  const handleUndo = useCallback((roomIndex: number, dateId: string, rateId: string) => {
    setResolutions((prev) => {
      const next = { ...prev };
      delete next[rateConflictKey(roomIndex, dateId, rateId)];
      return next;
    });
  }, []);

  const handleAddManualRoom = useCallback((catalogRoomId: string, catalogRoomName: string) => {
    setManualRooms((prev) => [...prev, { catalogRoomId, catalogRoomName, dates: [] }]);
  }, []);

  const handleRemoveManualRoom = useCallback((roomIndex: number) => {
    setManualRooms((prev) => prev.filter((_, i) => i !== roomIndex));
  }, []);

  const handleAddManualDate = useCallback(
    (roomIndex: number, start: string, end: string) => {
      setManualRooms((prev) => {
        const room = prev[roomIndex];
        const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
        const blackoutDates = catalogRoom?.blackoutDates ?? [];
        const rates = catalogRoom?.roomRates ?? [];
        const hasActionableConflict = rates.some((rate) => {
          const conflict = findRateConflict({ start, end }, rate.id, blackoutDates);
          return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
        });
        return prev.map((r, i) =>
          i === roomIndex
            ? { ...r, dates: [...r.dates, { id: uuid_v4(), start, end, selected: !hasActionableConflict }] }
            : r,
        );
      });
    },
    [catalogRooms],
  );

  const handleRemoveManualDate = useCallback((roomIndex: number, dateIndex: number) => {
    setManualRooms((prev) =>
      prev.map((room, i) =>
        i === roomIndex ? { ...room, dates: room.dates.filter((_, j) => j !== dateIndex) } : room,
      ),
    );
  }, []);

  const handleResolveManualDate = useCallback(
    (roomIndex: number, dateId: string, rateId: string, status: ConflictResolution) => {
      setManualResolutions((prev) => ({ ...prev, [rateConflictKey(roomIndex, dateId, rateId)]: status }));
    },
    [],
  );

  const handleUndoManualDate = useCallback((roomIndex: number, dateId: string, rateId: string) => {
    setManualResolutions((prev) => {
      const next = { ...prev };
      delete next[rateConflictKey(roomIndex, dateId, rateId)];
      return next;
    });
  }, []);

  const handleToggleManualDate = useCallback((roomIndex: number, dateIndex: number, selected: boolean) => {
    setManualRooms((prev) =>
      prev.map((room, i) =>
        i === roomIndex
          ? { ...room, dates: room.dates.map((d, j) => (j === dateIndex ? { ...d, selected } : d)) }
          : room,
      ),
    );
  }, []);

  const handleEditManualDate = useCallback(
    (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => {
      setManualRooms((prev) =>
        prev.map((room, i) => {
          if (i !== roomIndex) {
            return room;
          }
          const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
          const blackoutDates = catalogRoom?.blackoutDates ?? [];
          const rates = catalogRoom?.roomRates ?? [];
          const hasActionableConflict = rates.some((rate) => {
            const conflict = findRateConflict(newRange, rate.id, blackoutDates);
            return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
          });
          return {
            ...room,
            dates: room.dates.map((d) =>
              d.id === dateId ? { ...d, ...newRange, selected: !hasActionableConflict } : d,
            ),
          };
        }),
      );
      setManualResolutions((prev) => {
        const next = { ...prev };
        Object.keys(next)
          .filter((k) => k.startsWith(`${roomIndex}-${dateId}-`))
          .forEach((k) => delete next[k]);
        return next;
      });
    },
    [catalogRooms],
  );

  const handleEditDate = useCallback(
    (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
      const blackoutDates = catalogRoom?.blackoutDates ?? [];
      const rates = catalogRoom?.roomRates ?? [];
      const hasActionableConflict = rates.some((rate) => {
        const conflict = findRateConflict(newRange, rate.id, blackoutDates);
        return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
      });

      setLocalDates((prev) =>
        prev.map((room, ri) =>
          ri === roomIndex
            ? room.map((d) => (d.id === dateId ? { ...d, ...newRange, selected: !hasActionableConflict } : d))
            : room,
        ),
      );
      setResolutions((prev) => {
        const next = { ...prev };
        Object.keys(next)
          .filter((k) => k.startsWith(`${roomIndex}-${dateId}-`))
          .forEach((k) => delete next[k]);
        return next;
      });
    },
    [catalogRooms, extraction.rooms, roomTypeOverrides],
  );

  const handleDeleteDate = useCallback((roomIndex: number, dateId: string) => {
    // functional updaters only — localDates not captured
    setLocalDates((prev) => prev.map((room, ri) => (ri === roomIndex ? room.filter((d) => d.id !== dateId) : room)));
    setResolutions((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((k) => k.startsWith(`${roomIndex}-${dateId}-`))
        .forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  const handleAddExtractedDate = useCallback(
    (roomIndex: number, start: string, end: string) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
      const blackoutDates = catalogRoom?.blackoutDates ?? [];
      const rates = catalogRoom?.roomRates ?? [];
      const hasActionableConflict = rates.some((rate) => {
        const conflict = findRateConflict({ start, end }, rate.id, blackoutDates);
        return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
      });
      const newDate: ExtractedDateRange = { id: uuid_v4(), start, end, selected: !hasActionableConflict };
      setLocalDates((prev) => prev.map((room, ri) => (ri === roomIndex ? [...room, newDate] : room)));
    },
    [roomTypeOverrides, extraction.rooms, catalogRooms],
  );

  const handleApply = useCallback(() => {
    type RoomOperation = StopsellApplyPayload['rooms'][number]['operations'][number];

    const payload: StopsellApplyPayload = {
      rooms: localDates
        .map((roomDates, roomIndex) => {
          const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
          if (!roomTypeId) {
            return null;
          }

          const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
          const rates = catalogRoom?.roomRates ?? [];
          const blackoutDates = catalogRoom?.blackoutDates ?? [];

          const operations = roomDates.reduce<Array<RoomOperation>>((acc, date) => {
            if (!date.selected) {
              return acc;
            }

            const { blocksToDelete, rangesToCreate } = buildRateOperations(
              date,
              rates,
              blackoutDates,
              (rateId) => resolutions[rateConflictKey(roomIndex, date.id, rateId)] ?? null,
            );

            if (rangesToCreate.length === 0) {
              return acc;
            }
            acc.push({ operation: 'blackout', blocksToDelete, rangesToCreate });
            return acc;
          }, []);

          if (operations.length === 0) {
            return null;
          }
          return { roomTypeId, operations };
        })
        .filter((room): room is NonNullable<typeof room> => room !== null),
    };

    const manualEntries = manualRooms
      .map((room, origRoomIndex) => ({ room, origRoomIndex }))
      .filter(({ room }) => room.dates.some((d) => d.selected))
      .map(({ room, origRoomIndex }) => {
        const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
        const rates = catalogRoom?.roomRates ?? [];
        const blackoutDates = catalogRoom?.blackoutDates ?? [];
        return {
          roomTypeId: room.catalogRoomId,
          operations: room.dates
            .map((d, origDateIndex) => ({ d, origDateIndex }))
            .filter(({ d }) => d.selected)
            .map(({ d, origDateIndex }) => {
              const { blocksToDelete, rangesToCreate } = buildRateOperations(
                d,
                rates,
                blackoutDates,
                (rateId) => manualResolutions[rateConflictKey(origRoomIndex, d.id, rateId)] ?? null,
              );

              return { operation: 'blackout' as const, blocksToDelete, rangesToCreate };
            })
            .filter((op) => op.rangesToCreate.length > 0),
        };
      })
      .filter((room) => room.operations.length > 0);

    const extractedRoomTypeIdSet = new Set(payload.rooms.map((r) => r.roomTypeId));
    payload.rooms.push(...manualEntries.filter((e) => !extractedRoomTypeIdSet.has(e.roomTypeId)));

    onApply(payload);
  }, [
    localDates,
    extraction.rooms,
    onApply,
    roomTypeOverrides,
    resolutions,
    manualRooms,
    manualResolutions,
    catalogRooms,
  ]);

  const confirmSummary = useMemo(
    () =>
      confirmOpen
        ? buildStopsellConfirmSummary(
            extraction,
            localDates,
            rateConflicts,
            resolutions,
            catalogRooms,
            roomTypeOverrides,
            manualRooms,
            manualResolutions,
          )
        : { rooms: [], stats: [] },
    [
      confirmOpen,
      extraction,
      localDates,
      rateConflicts,
      resolutions,
      catalogRooms,
      roomTypeOverrides,
      manualRooms,
      manualResolutions,
    ],
  );

  const renderManualCard = useCallback(
    (room: ManualRoom, roomIndex: number) => {
      const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
      const rateResolutions = sliceResolutionsByRoom(manualResolutions, roomIndex);
      return (
        <StopsellManualRoomCard
          key={room.catalogRoomId}
          room={room}
          roomIndex={roomIndex}
          blackoutDates={catalogRoom?.blackoutDates ?? []}
          catalogRoomRates={catalogRoom?.roomRates ?? []}
          rateResolutions={rateResolutions}
          onAddDate={handleAddManualDate}
          onEditDate={handleEditManualDate}
          onRemoveRoom={handleRemoveManualRoom}
          onRemoveDate={handleRemoveManualDate}
          onToggleDate={handleToggleManualDate}
          onResolve={handleResolveManualDate}
          onUndo={handleUndoManualDate}
        />
      );
    },
    [
      catalogRooms,
      manualResolutions,
      handleAddManualDate,
      handleEditManualDate,
      handleRemoveManualRoom,
      handleRemoveManualDate,
      handleToggleManualDate,
      handleResolveManualDate,
      handleUndoManualDate,
    ],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Stopsell Dates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        {isBlackoutError ? (
          <Alert severity="error">Could not load existing blackout dates. Please close and try again.</Alert>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                mb: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAllChange}
                  />
                }
                label={<Typography fontWeight={500}>Select all rooms</Typography>}
              />
            </Box>

            <Stack gap={1.5}>
              {extraction.rooms.map((room, roomIndex) => (
                <StopsellRoomGroupRow
                  key={`${room.name}-${roomIndex}`}
                  roomIndex={roomIndex}
                  room={room}
                  localDates={localDates[roomIndex] ?? []}
                  catalogRooms={catalogRooms}
                  selectedRoomTypeId={roomTypeOverrides[roomIndex] ?? room.roomTypeId ?? ''}
                  resolutions={resolutions}
                  onDateChange={handleDateChange}
                  onToggleAll={handleRoomToggleAll}
                  onRoomTypeChange={handleRoomTypeOverride}
                  onResolve={handleResolve}
                  onUndo={handleUndo}
                  onEditDate={handleEditDate}
                  onDeleteDate={handleDeleteDate}
                  onAddExtractedDate={handleAddExtractedDate}
                />
              ))}
            </Stack>

            <AvailabilityManualRoomsSection
              catalogRooms={catalogRooms}
              extractedRoomTypeIds={extractedRoomTypeIds}
              manualRooms={manualRooms}
              onAddRoom={handleAddManualRoom}
              renderCard={renderManualCard}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          {unresolvedConflicts > 0 && (
            <Typography variant="body2" color="warning.dark">
              <strong>
                {unresolvedConflicts} unresolved {unresolvedConflicts === 1 ? 'conflict' : 'conflicts'}
              </strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <strong>{pluralizeToString('room', selectedRoomCount)}</strong>,{' '}
            <strong>{pluralizeToString('date range', selectedDateCount)}</strong> selected
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip
            title={
              isBlackoutError
                ? 'Blackout dates could not be loaded. Please close and try again.'
                : anySelected && !allSelectedRoomsMapped
                  ? 'All selected rooms must be mapped to a room type'
                  : unresolvedConflicts > 0
                    ? 'Resolve all conflicts before applying'
                    : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                disabled={
                  isApplying || isBlackoutError || !anySelected || !allSelectedRoomsMapped || unresolvedConflicts > 0
                }
              >
                Review & Apply →
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>

      <AvailabilityConfirmModal
        open={confirmOpen}
        onBack={() => setConfirmOpen(false)}
        onConfirm={handleApply}
        summary={confirmSummary}
        title="Stopsell"
        description="Review what will be applied. Overrides will delete existing blackout blocks before creating new ones."
        isApplying={isApplying}
      />
    </Dialog>
  );
}
